import {
  ABILITY_KEYS,
  CR_TABLE,
  abilityMod,
  abilityScore,
  computeAc,
  computeHp,
  formatMod,
  hasSaveProficiency,
  initiativeBonus,
  passivePerception,
  proficiency,
  saveBonus,
  skillBonus,
  type AbilityKey,
  type Statblock,
  type StatblockEntry,
} from './statblock.js';

/**
 * Bezeichnungen für Statblöcke. Werte aus dem Monster-Maker (englische Schlüssel
 * wie `small`, `perception`, `fire`) werden übersetzt, freie Texte bleiben unverändert.
 * Für weitere Sprachen ein Objekt mit denselben Schlüsseln ergänzen.
 */
export const STATBLOCK_DE = {
  labels: {
    ac: 'Rüstungsklasse',
    acShort: 'RK',
    hp: 'Trefferpunkte',
    hpShort: 'TP',
    speed: 'Bewegungsrate',
    initiative: 'Initiative',
    saves: 'Rettungswürfe',
    skills: 'Fertigkeiten',
    vulnerabilities: 'Schadensanfälligkeiten',
    resistances: 'Schadensresistenzen',
    immunities: 'Schadensimmunitäten',
    conditionImmunities: 'Zustandsimmunitäten',
    vulnerabilities2024: 'Anfälligkeiten',
    resistances2024: 'Resistenzen',
    immunities2024: 'Immunitäten',
    senses: 'Sinne',
    passivePerception: 'Passive Wahrnehmung',
    languages: 'Sprachen',
    challenge: 'Herausforderungsgrad',
    challengeShort: 'HG',
    xp: 'EP',
    pb: 'ÜB',
    traits: 'Merkmale',
    actions: 'Aktionen',
    bonusActions: 'Bonusaktionen',
    reactions: 'Reaktionen',
    legendaries: 'Legendäre Aktionen',
    mythics: 'Mythische Aktionen',
    lairs: 'Hortaktionen',
    regionals: 'Regionale Effekte',
    mod: 'MOD',
    save: 'RW',
    shield: 'Schild',
    withMageArmor: 'mit Magierrüstung',
    blindBeyond: 'blind außerhalb dieses Radius',
    telepathy: 'Telepathie',
    understands: 'versteht',
    cantSpeak: 'kann aber nicht sprechen',
    hover: 'schwebend',
    none: '—',
  },
  abilities: { str: 'STÄ', dex: 'GES', con: 'KON', int: 'INT', wis: 'WEI', cha: 'CHA' } as Record<AbilityKey, string>,
  abilityNames: {
    str: 'Stärke',
    dex: 'Geschicklichkeit',
    con: 'Konstitution',
    int: 'Intelligenz',
    wis: 'Weisheit',
    cha: 'Charisma',
  } as Record<AbilityKey, string>,
  sizes: { tiny: 'Winziger', small: 'Kleiner', medium: 'Mittelgroßer', large: 'Großer', huge: 'Riesiger', gargantuan: 'Gigantischer' } as Record<string, string>,
  sizeNames: { tiny: 'Winzig', small: 'Klein', medium: 'Mittelgroß', large: 'Groß', huge: 'Riesig', gargantuan: 'Gigantisch' } as Record<string, string>,
  speeds: { burrow: 'Graben', climb: 'Klettern', fly: 'Fliegen', swim: 'Schwimmen' },
  senses: { blindsight: 'Blindsicht', darkvision: 'Dunkelsicht', tremorsense: 'Erschütterungssinn', truesight: 'Wahrsicht' },
  skills: {
    acrobatics: 'Akrobatik',
    'animal handling': 'Mit Tieren umgehen',
    arcana: 'Arkane Kunde',
    athletics: 'Athletik',
    deception: 'Täuschen',
    history: 'Geschichte',
    insight: 'Motiv erkennen',
    intimidation: 'Einschüchtern',
    investigation: 'Nachforschungen',
    medicine: 'Heilkunde',
    nature: 'Naturkunde',
    perception: 'Wahrnehmung',
    performance: 'Auftreten',
    persuasion: 'Überzeugen',
    religion: 'Religion',
    'sleight of hand': 'Fingerfertigkeit',
    stealth: 'Heimlichkeit',
    survival: 'Überlebenskunst',
  } as Record<string, string>,
  damage: {
    acid: 'Säure',
    bludgeoning: 'Wucht',
    cold: 'Kälte',
    fire: 'Feuer',
    force: 'Energie',
    lightning: 'Blitz',
    necrotic: 'Nekrotisch',
    piercing: 'Stich',
    poison: 'Gift',
    psychic: 'Psychisch',
    radiant: 'Gleißend',
    slashing: 'Hieb',
    thunder: 'Schall',
  } as Record<string, string>,
  conditions: {
    blinded: 'Blind',
    charmed: 'Bezaubert',
    deafened: 'Taub',
    exhaustion: 'Erschöpfung',
    frightened: 'Verängstigt',
    grappled: 'Gepackt',
    incapacitated: 'Kampfunfähig',
    invisible: 'Unsichtbar',
    paralyzed: 'Gelähmt',
    petrified: 'Versteinert',
    poisoned: 'Vergiftet',
    prone: 'Liegend',
    restrained: 'Festgesetzt',
    stunned: 'Betäubt',
    unconscious: 'Bewusstlos',
    dead: 'Verstorben',
  } as Record<string, string>,
  armors: {
    none: 'keine',
    'natural armor': 'natürliche Rüstung',
    'mage armor': 'Magierrüstung',
    'padded armor': 'gepolsterte Rüstung',
    'leather armor': 'Lederrüstung',
    'studded leather': 'beschlagenes Leder',
    'hide armor': 'Fellrüstung',
    'chain shirt': 'Kettenhemd',
    'scale mail': 'Schuppenpanzer',
    breastplate: 'Brustplatte',
    'half plate': 'Halbplatte',
    'ring mail': 'Ringpanzer',
    'chain mail': 'Kettenpanzer',
    splint: 'Schienenpanzer',
    plate: 'Ritterrüstung',
    other: 'andere',
  } as Record<string, string>,
};

export type StatblockDict = typeof STATBLOCK_DE;

const tr = (map: Record<string, string>, v: string) => map[v.toLowerCase()] ?? v;
const num = (n: number) => n.toLocaleString('de-DE');

export interface FormattedStatblock {
  name: string;
  subtitle: string;
  format: '2014' | '2024';
  ac: number;
  acText: string;
  hp: number;
  hpText: string;
  speedText: string;
  initiative: number;
  initiativeText: string;
  abilities: { key: AbilityKey; label: string; score: number; mod: string; save: string; proficient: boolean }[];
  /** Eigenschaftszeilen (Label + Text) in Reihenfolge des Statblocks. */
  lines: { label: string; text: string }[];
  sections: { key: string; title: string; intro?: string; entries: StatblockEntry[]; outro?: string }[];
}

/** Bereitet einen Statblock zur Anzeige auf (Text mit deutschen Bezeichnungen). */
export function formatStatblock(sb: Statblock, d: StatblockDict = STATBLOCK_DE): FormattedStatblock {
  const L = d.labels;
  const format = sb.format === '2024' ? '2024' : '2014';
  const size = (sb.size ?? 'medium').toLowerCase();
  const type = sb.type ?? '';
  const subtitle =
    `${format === '2024' ? (d.sizeNames[size] ?? sb.size ?? '') : (d.sizes[size] ?? sb.size ?? '')} ${type}`.trim() +
    (sb.tag ? ` (${sb.tag})` : '') +
    (sb.alignment ? `, ${sb.alignment}` : '');

  const acInfo = computeAc(sb);
  let acText = String(acInfo.ac);
  if (sb.armorName === 'other') acText = sb.otherArmorDesc || acText;
  else {
    const parts: string[] = [];
    if (acInfo.armor) parts.push(tr(d.armors, acInfo.armor));
    if (acInfo.shield) parts.push(L.shield);
    if (parts.length) acText += ` (${parts.join(', ')})`;
    if (acInfo.mageArmorAc !== null) acText += ` (${acInfo.mageArmorAc} ${L.withMageArmor})`;
  }

  const hpInfo = computeHp(sb);
  const hpText = sb.customHP ? (sb.hpText ?? String(hpInfo.hp)) : `${hpInfo.hp} (${hpInfo.formula})`;

  let speedText: string;
  if (sb.customSpeed && sb.speedDesc) speedText = sb.speedDesc;
  else {
    const sp = [`${sb.speed ?? 30} ft.`];
    if (sb.burrowSpeed) sp.push(`${d.speeds.burrow} ${sb.burrowSpeed} ft.`);
    if (sb.climbSpeed) sp.push(`${d.speeds.climb} ${sb.climbSpeed} ft.`);
    if (sb.flySpeed) sp.push(`${d.speeds.fly} ${sb.flySpeed} ft.${sb.hover ? ` (${L.hover})` : ''}`);
    if (sb.swimSpeed) sp.push(`${d.speeds.swim} ${sb.swimSpeed} ft.`);
    speedText = sp.join(', ');
  }

  const init = initiativeBonus(sb);
  const abilities = ABILITY_KEYS.map((key) => {
    const score = abilityScore(sb, key);
    return {
      key,
      label: d.abilities[key],
      score,
      mod: formatMod(abilityMod(score)),
      save: formatMod(saveBonus(sb, key)),
      proficient: hasSaveProficiency(sb, key),
    };
  });

  const lines: { label: string; text: string }[] = [];
  if (format === '2014') {
    const saves = ABILITY_KEYS.filter((k) => hasSaveProficiency(sb, k)).map((k) => `${d.abilities[k]} ${formatMod(saveBonus(sb, k))}`);
    if (saves.length) lines.push({ label: L.saves, text: saves.join(', ') });
  }
  if (sb.skills?.length) {
    lines.push({ label: L.skills, text: sb.skills.map((s) => `${tr(d.skills, s.name)} ${formatMod(skillBonus(sb, s))}`).join(', ') });
  }
  const dmg = (type: 'v' | 'r' | 'i') => {
    const basic = (sb.damagetypes ?? []).filter((x) => x.type === type).map((x) => tr(d.damage, x.name));
    const special = (sb.specialdamage ?? []).filter((x) => x.type === type).map((x) => x.name);
    return [basic.join(', '), ...special].filter(Boolean).join('; ');
  };
  const conds = (sb.conditions ?? []).map((c) => tr(d.conditions, c.name)).join(', ');
  const vul = dmg('v');
  const res = dmg('r');
  const imm = dmg('i');
  if (format === '2014') {
    if (vul) lines.push({ label: L.vulnerabilities, text: vul });
    if (res) lines.push({ label: L.resistances, text: res });
    if (imm) lines.push({ label: L.immunities, text: imm });
    if (conds) lines.push({ label: L.conditionImmunities, text: conds });
  } else {
    if (vul) lines.push({ label: L.vulnerabilities2024, text: vul });
    if (res) lines.push({ label: L.resistances2024, text: res });
    const all = [imm, conds].filter(Boolean).join('; ');
    if (all) lines.push({ label: L.immunities2024, text: all });
  }
  const senses: string[] = [];
  if (sb.blindsight) senses.push(`${d.senses.blindsight} ${sb.blindsight} ft.${sb.blind ? ` (${L.blindBeyond})` : ''}`);
  if (sb.darkvision) senses.push(`${d.senses.darkvision} ${sb.darkvision} ft.`);
  if (sb.tremorsense) senses.push(`${d.senses.tremorsense} ${sb.tremorsense} ft.`);
  if (sb.truesight) senses.push(`${d.senses.truesight} ${sb.truesight} ft.`);
  senses.push(`${L.passivePerception} ${passivePerception(sb)}`);
  lines.push({ label: L.senses, text: senses.join(', ') });

  const speaks = (sb.languages ?? []).filter((l) => l.speaks).map((l) => l.name);
  const understands = (sb.languages ?? []).filter((l) => !l.speaks).map((l) => l.name);
  const lang: string[] = [];
  if (speaks.length) lang.push(speaks.join(', '));
  if (understands.length) lang.push(`${L.understands} ${understands.join(', ')}${sb.understandsBut ? ` ${sb.understandsBut}` : `, ${L.cantSpeak}`}`);
  if (sb.telepathy) lang.push(`${L.telepathy} ${sb.telepathy} ft.`);
  lines.push({ label: L.languages, text: lang.join('; ') || L.none });

  if (sb.cr) {
    const prof = proficiency(sb);
    const row = sb.cr === '*' ? null : CR_TABLE[sb.cr];
    const crLabel = sb.cr === '*' ? (sb.customCr ?? '') : sb.cr;
    if (format === '2014') {
      lines.push({ label: L.challenge, text: row ? `${crLabel} (${num(row.xp)} ${L.xp})` : crLabel });
    } else {
      lines.push({ label: L.challengeShort, text: row ? `${crLabel} (${L.xp} ${num(row.xp)}; ${L.pb} ${formatMod(prof)})` : `${crLabel} (${L.pb} ${formatMod(prof)})` });
    }
  }

  const sections: FormattedStatblock['sections'] = [];
  if (sb.abilities?.length) sections.push({ key: 'abilities', title: format === '2024' ? L.traits : '', entries: sb.abilities });
  if (sb.actions?.length) sections.push({ key: 'actions', title: L.actions, entries: sb.actions });
  if (sb.bonusActions?.length) sections.push({ key: 'bonusActions', title: L.bonusActions, entries: sb.bonusActions });
  if (sb.reactions?.length) sections.push({ key: 'reactions', title: L.reactions, entries: sb.reactions });
  if (sb.isLegendary) sections.push({ key: 'legendaries', title: L.legendaries, intro: sb.legendariesDescription, entries: sb.legendaries ?? [] });
  if (sb.isMythic) sections.push({ key: 'mythics', title: L.mythics, intro: sb.mythicDescription, entries: sb.mythics ?? [] });
  if (sb.isLair) sections.push({ key: 'lairs', title: L.lairs, intro: sb.lairDescription, entries: sb.lairs ?? [], outro: sb.lairDescriptionEnd });
  if (sb.isRegional) {
    sections.push({ key: 'regionals', title: L.regionals, intro: sb.regionalDescription, entries: sb.regionals ?? [], outro: sb.regionalDescriptionEnd });
  }

  return {
    name: sb.name ?? '',
    subtitle,
    format,
    ac: acInfo.ac,
    acText,
    hp: hpInfo.hp,
    hpText,
    speedText,
    initiative: init,
    initiativeText: `${formatMod(init)} (${10 + init})`,
    abilities,
    lines,
    sections,
  };
}
